"use client";

import HeroSection from "@/components/HeroSection";
import Image from "next/image";
import { useState } from "react";
import { PricingSection } from "@/components/PricingSection";
import EnterpriseContact from "@/components/EnterpriseContact";
import AutoplayYouTube from "@/components/AutoplayYouTube";
import ClientLogos from "@/components/ClientLogos";
import { ROUTES } from "@/lib/routes";
import FAQSection, { type FAQItem } from "@/components/FAQSection";
import MarketplaceOptimizationSection from "@/components/MarketplaceOptimizationSection";
import ProductVariantsSection from "@/components/ProductVariantsSection";
import NewsletterSignupSection from "@/components/NewsletterSignupSection";

export const homeFAQItems: FAQItem[] = [
  {
    question: "What exactly does this tool do?",
    answer:
      "Speedy Listing helps you create scroll-stopping Amazon images, video scripts, A+ modules, customer avatars, competitive breakdowns, and complete creative briefs instantly. You get real, usable creative direction - not generic fluff - in seconds.",
  },
  {
    question: "Do I need any design or AI experience?",
    answer:
      "Not at all. You simply upload your product photo or paste your ASIN. The tool does the heavy lifting and delivers ready-to-use concepts your designer or photographer can execute.",
  },
  {
    question: "Will this actually help my CTR and conversion rate?",
    answer:
      'Yes. The tool is built around proven Amazon creative strategies that have increased CTR and sales for thousands of listings. You\'re not just getting "AI images" - you\'re getting data-driven recommendations based on strategies AMZ One Step has used for 4,000+ brands.',
  },
  {
    question: "Can the tool replace a designer?",
    answer:
      "It gives your designer a perfect blueprint, so they spend less time guessing and more time producing work that performs. Many users say it cuts their creative cycle down by 70-90%.",
  },
  {
    question: "Do the images generated follow Amazon's rules?",
    answer:
      "Yes. All prompts, concepts, layouts, and outputs are built with Amazon TOS in mind:\n- white background\n- 85% fill\n- no claims\n- no misleading effects\n- accurate product representation\n\nYou stay compliant while maximizing performance.",
  },
  {
    question: "Does it support every product category?",
    answer:
      "Yes - supplements, beauty, kitchen, tools, electronics, baby, pet, home, outdoors, and more. The AI tailors each brief to your category automatically.",
  },
  {
    question: "Can I generate full image sets, not just one image?",
    answer:
      "Absolutely. You get:\n- 3 main image concepts\n- 4-6 infographic concepts\n- 2 lifestyle scenes\n- 1 A+ content plan\n- 1 video screenplay\n\nAll generated from just your ASIN or PDF.",
  },
  {
    question: "Will it create customer avatars too?",
    answer:
      "Yes - and this is one of the most powerful parts. The AI scans your reviews, competitor reviews, and keywords to build:\n- pains\n- desires\n- objections\n- buying triggers\n- demographic insights\n\nThis becomes the foundation for your entire creative strategy.",
  },
  {
    question: "How accurate are the competitive insights?",
    answer:
      "Very accurate. The tool breaks down what your competitors are doing well, what they're missing, and what opportunities you can capitalize on.",
  },
  {
    question: "How long does it take to generate everything?",
    answer: "Usually 10 minutes depending on the plan and type of output.",
  },
  {
    question: "Does this replace split testing?",
    answer:
      "No. But it gives you high-probability winners you can take straight into Manage Experiments or ProductPinion. Our users see 2-4x higher success rates in their tests because the concepts are data-driven.",
  },
  {
    question: "Do designers actually like this tool?",
    answer:
      "Most designers love it because it removes guesswork and gives them a clear direction. Agencies especially use it to speed up delivery and improve consistency.",
  },
  {
    question: "Is my data private?",
    answer:
      "Yes for paid plans. Your ASINs, PDFs, and concepts are private. Nothing is publicly shared and no one else has access to your materials.",
  },
  {
    question: "Do you offer support if I get stuck?",
    answer: "Yes - you can reach us anytime, and we'll walk you through everything or even hop on a quick call if needed.",
  },
  {
    question: "Can I collaborate with my team inside the tool?",
    answer: "Yes - you can invite team members, share briefs, and collaborate in one place.",
  },
];

// ============================================
// STATS SECTION
// ============================================
function StatsSection() {
  const stats = [
    {
      value: "84",
      label: "Hours saved per listing",
      icon: "/group3/hourssavedperlisting.png",
    },
    {
      value: "500k+",
      label: "USD saved per year for large brands",
      icon: "/group3/USD saved per year for large brands.png",
    },
    {
      value: "27%",
      label: "Increase in conversion rate",
      icon: "/group3/Increase in conversion rate.png",
    },
  ];

  return (
    <section
      className="py-16"
      style={{
        background: "linear-gradient(180deg, rgba(255, 255, 255, 0.7) 0%, rgba(242, 244, 255, 0.7) 17.47%, rgba(255, 255, 255, 0.7) 100%)"
      }}
    >
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex flex-col md:flex-row gap-6">
          {stats.map((stat) => (
            <div
              key={stat.value}
              className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/80 flex-1"
              style={{
                boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(37,99,235,0.06)",
              }}
            >
              <div className="flex justify-center mb-5 h-20">
                <Image src={stat.icon} alt={stat.label} width={140} height={140} className="object-contain" />
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                  fontSize: 42,
                  fontWeight: 700,
                  color: "#1B1C30",
                  letterSpacing: "-0.03em",
                  lineHeight: 1.1,
                  marginBottom: 8,
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#5C5F73",
                  lineHeight: 1.5,
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// HOW IT WORKS SECTION
// ============================================
function HowItWorks() {
  const steps = [
    {
      number: "1",
      title: "Enter ASIN",
      description: "Paste any Amazon product link",
      visual: (
        <Image
          className="h-[200px] w-auto max-w-full object-contain object-top mx-auto"
          src="/group4/enteraspian.png"
          alt="Enter ASIN - main card"
          width={400}
          height={300}
        />
      ),
    },
    {
      number: "2",
      title: "AI Analysis",
      description: "Reviews, competitors, keywords",
      visual: (
        <Image
          src="/group4/ai analysis.png"
          alt="AI Analysis - competitors, reviews, keywords"
          className="h-[200px] w-auto max-w-full object-contain object-top mx-auto"
          width={400}
          height={300}
        />
      ),
    },
    {
      number: "3",
      title: "Generate Creatives",
      description: "Images, infographics, A+ Content",
      visual: (
        <Image
          src="/group4/generative creative.png"
          alt="Generate Creatives - Images, Infographics, A+ Content"
          className="h-[200px] w-auto max-w-full object-contain object-top mx-auto"
          width={400}
          height={300}
        />
      ),
    },
    {
      number: "4",
      title: "Direct Upload",
      description: "Upload directly to seller central with 1 click",
      visual: (
        <Image
          src="/Frame 2147226435.png"
          alt="Direct Upload - upload directly to seller central"
          className="h-[200px] w-auto max-w-full object-contain object-top mx-auto"
          width={510}
          height={576}
        />
      ),
    },
  ];

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-[1280px] mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-14">
          <div
            className="inline-block px-4 py-1.5 rounded-full text-[12px] font-semibold mb-5 tracking-wide"
            style={{
              background: "rgba(20, 100, 255, 0.06)",
              color: "#5C5F73",
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
            }}
          >
            How it Works
          </div>
          <h2
            style={{
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
              fontSize: "clamp(28px, 3vw, 40px)",
              fontWeight: 600,
              color: "#1B1C30",
              letterSpacing: "-0.02em",
              lineHeight: 1.25,
              marginBottom: 12,
            }}
          >
            How <span style={{
              background: "linear-gradient(180deg, #93A2FF 0%, #3647FF 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent"
            }}>Speedy Listing</span> Works
          </h2>
          <p
            style={{
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
              fontSize: 15,
              lineHeight: 1.6,
            }}
          >
            Generate high-converting Amazon images in just a few simple steps.
          </p>
        </div>

        {/* Steps */}
        <div className="flex flex-col md:flex-row gap-8 md:gap-0">
          {steps.map((step) => (
            <div
              key={step.number}
              className="bg-transparent rounded-2xl px-2 py-4 flex-1"
            >
              <div className="flex flex-col items-center mb-6">
                <div
                  className="w-8 h-8 rounded-full border border-[#E0EAF4] flex items-center justify-center text-sm font-medium text-[#1B1C30] mb-3"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {step.number}
                </div>
                <h3
                  className="text-center md:text-left"
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontSize: "clamp(18px, 3vw, 32px)",
                    fontWeight: 700,
                    color: "#1B1C30",
                    whiteSpace: 'nowrap',
                  }}
                >
                  {step.title}
                </h3>
                <p
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontSize: 16,
                    color: "#4D4D5C",
                    textAlign: 'center',
                  }}
                >
                  {step.description}
                </p>
              </div>
              <div className="h-[220px] flex items-start justify-center">
                {step.visual}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// LISTING SHOWCASE SECTION
// ============================================
function ListingShowcase() {
  return (
    <section className="pt-16 pb-20 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6">
        <div
          className="flex items-center justify-center z-10"
          style={{
            background: "linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 60%, transparent 100%)",
            paddingTop: 80,
            paddingBottom: 16,
          }}
        >
          <h2              
            style={{
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
              fontSize: "clamp(28px, 3vw, 40px)",
              fontWeight: 600,
              color: "black",
              letterSpacing: "-0.02em",
            }}
          >
            See Why Everyone Is Talking About This Product
          </h2>
        </div>
        {/* Video Embed with Play Button */}
        <div className="relative rounded-2xl overflow-hidden shadow-lg">
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <AutoplayYouTube
              className="absolute inset-0 w-full h-full"
              videoId="e0y_HHWMnx0"
              title="Speedy Listing Video"
            />
          </div>
          {/* Heading overlay at bottom with full width blend */}
        </div>
      </div>
    </section>
  );
}

// ============================================
// CUSTOMER REVIEWS SECTION
// ============================================
function CustomerReviews() {
  const productImages = [
    "image-01.png",
    "image-02.png",
    "image-03.png",
    "image-04.png",
    "image-05.png",
    "image-06.png",
    "image-07.png",
    "image-08.png",
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-12 items-start mb-14">
          {/* Left Content */}
          <div className="pt-2 flex-1">
            <h2
              style={{
                fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                fontSize: "clamp(28px, 3vw, 40px)",
                fontWeight: 700,
                color: "#1B1C30",
                letterSpacing: "-0.03em",
                lineHeight: 1.2,
                marginBottom: 16,
              }}
            >
              Built From Real <br className="hidden md:block" />
              Customer Reviews
            </h2>
            <p
              style={{
                fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                fontSize: 14,
                color: "#5C5F73",
                lineHeight: 1.7,
                marginBottom: 28,
                maxWidth: 420,
              }}
            >
              The platform scans Amazon reviews to identify the exact problems and desires customers mention most. Your
              images are then generated around those insights so they instantly resonate with buyers.
            </p>
            <button
              onClick={() => {
                window.location.href = ROUTES.homePricing;
              }}
              className="inline-flex items-center gap-2 rounded-full transition-colors hover:bg-[#0D4FD6]"
              style={{
                fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "#FFFFFF",
                background: "#1464FF",
                padding: "10px 24px",
              }}
            >
              Start Free
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M5.5 3L9.5 7L5.5 11"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Right - Review Card Image */}
          <div className="relative flex-1">
            <Image
              src="/group5/build for real customers reviews .png"
              alt="Build for real customers reviews"
              width={500}
              height={350}
              className="object-contain"
            />
          </div>
        </div>

        {/* Product Image Grid - 4 columns x 2 rows */}
        <div className="flex flex-wrap gap-3">
          {productImages.map((img, i) => (
            <div
              key={i}
              className="aspect-square rounded-xl overflow-hidden relative group w-[calc(50%-6px)] sm:w-[calc(25%-9px)]"
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
            >
              <Image
                src={`/product-images/${img}`}
                alt={`Product listing example ${i + 1}`}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// A+ MODULES SECTION
// ============================================
function APlusModules() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-[1200] mx-auto px-6 h-[400px] md:bg-[url(/aplus-modules.png)] bg-contain bg-no-repeat bg-right">
        <div className="flex flex-col lg:flex-row gap-12 items-top">
          {/* Left - Content */}
          <div className="flex-1">
            <h2
              className="mb-6"
              style={{
                fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                fontSize: "clamp(28px, 3vw, 40px)",
                fontWeight: 700,
                color: "black",
                letterSpacing: "-0.03em",
                lineHeight: 1.2,
              }}
            >
              A+ Modules Built to Convert
            </h2>
            <p className="text-md text-gray-600 mb-8">
              Speedy Listing generates high-converting A+ content using proven module layouts designed for
              modern Amazon shoppers. From comparison charts to benefit-driven sections and mobile-friendly visuals.
            </p>
            <button
              onClick={() => {
                window.location.href = ROUTES.homePricing;
              }}
              className="inline-flex items-center gap-2 rounded-full transition-colors hover:bg-[#0D4FD6]"
              style={{
                fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "#FFFFFF",
                background: "#1464FF",
                padding: "10px 24px",
              }}
            >
              Generate A+ Content
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M5.5 3L9.5 7L5.5 11"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

            </button>
          </div>

          {/* Right - A+ Content Image */}
          <div className="hidden md:flex flex-1">
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// AMAZON EXPERTS SECTION
// ============================================
function AmazonExperts() {
  const typicalTools = ["Random AI Generation", "Generic AI Output", "No Context", "Hallucinate"];

  const platformFeatures = [
    {
      title: "Built for Amazon Performance",
      description:
        "Proprietary AI trained exclusively on proven Amazon frameworks to engineer CTR-optimized visual assets.",
      icon: "/experts/amazon.png",
    },
    {
      title: "Trained on Winning Frameworks",
      description: "Models strictly calibrated against top-converting Amazon architectures to execute proven visual hierarchies.",
      icon: "/experts/Frame 99.png",
    },
    {
      title: "CTR + CVR Focused Assets",
      description: "High-fidelity visual protocols engineered to maximize engagement & accelerate transaction completion.",
      icon: "/experts/Frame 99 (1).png",
    },
  ];

  const systemSteps = [
    { num: "1", label: "Decades of Learnings" },
    { num: "2", label: "Winning Patterns" },
    { num: "3", label: "AI Execution" },
    { num: "4", label: "Conversion-Focused Output" },
  ];

  return (
    <section className="flex w-full flex-col items-start gap-10 bg-[linear-gradient(180deg,rgba(255,255,255,0.5)_0%,rgba(207,230,255,0.5)_55.77%,rgba(255,255,255,0.5)_100%)] px-6 py-16 md:px-20 md:py-[120px]">
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-10">
        <h2 className="w-full text-center font-display text-[clamp(28px,3vw,40px)] font-bold leading-[1.3] text-[#1B1C30]">
          Built by <span className="text-[#FF9900]">Amazon Experts</span>.<br /> Not Generic AI
        </h2>

        <div className="box-border flex w-full flex-col overflow-hidden rounded-3xl border-2 border-white bg-white shadow-[0_0_12px_rgba(0,0,0,0.03)] lg:h-[352px] lg:flex-row">
          <div className="flex flex-col items-start gap-4 p-6 lg:w-[271px] lg:shrink-0">
            <h3 className="w-full font-display text-[clamp(24px,2.4vw,32px)] font-bold leading-[1.3125] text-[#1B1C30]">
              Typical Tools
            </h3>
            <div className="flex w-full flex-1 flex-col justify-end gap-4 pt-8 lg:pt-[72px]">
              {typicalTools.map((item) => (
                <p
                  key={item}
                  className="font-display text-[clamp(16px,1.4vw,18px)] font-semibold leading-[1.2778] text-[#1B1C30]"
                >
                  {item}
                </p>
              ))}
            </div>
          </div>

          <div className="flex flex-1 flex-col items-start gap-4 bg-[linear-gradient(180deg,rgba(167,197,255,0.08)_0%,rgba(0,57,167,0.08)_47.6%,rgba(167,197,255,0.08)_100%)] p-6">
            <h3 className="w-full font-display text-[clamp(24px,2.4vw,32px)] font-bold leading-[1.3125] text-[#1B1C30]">
              This Platform
            </h3>
            <div className="flex w-full flex-col gap-7 sm:flex-row lg:gap-11">
              {platformFeatures.map((feature) => (
                <div
                  key={feature.title}
                  className="flex min-w-0 flex-1 flex-col items-start gap-4 py-6"
                >
                  <div className="h-12 w-12">
                    <Image src={feature.icon} alt={feature.title} width={48} height={48} />
                  </div>
                  <h4 className="font-display text-[clamp(18px,1.6vw,22px)] font-semibold leading-[1.3182] text-[#1B1C30]">
                    {feature.title}
                  </h4>
                  <p className="font-display text-[15px] font-normal leading-[1.3333] text-[#424355]">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-12 py-6 md:py-[60px]">
          <div className="flex w-full items-center justify-center gap-3">
            <span className="font-display text-[clamp(18px,1.7vw,22px)] font-semibold leading-[1.3182] text-[#1B1C30]">
              Agency
            </span>
            <svg width="24" height="14" viewBox="0 0 20 12" fill="none" aria-hidden="true">
              <path
                d="M1 6H18M18 6L13 1M18 6L13 11"
                stroke="#8B8FA3"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="font-display text-[clamp(18px,1.7vw,22px)] font-semibold leading-[1.3182] text-[#1B1C30]">
              AI System
            </span>
          </div>

          <h3 className="w-full text-center font-display text-[clamp(24px,2.4vw,32px)] font-bold leading-[1.3125] text-[#1B1C30]">
            Your Competitors use a tool, You Use a System
          </h3>

          <div className="flex w-full max-w-[898px] flex-col gap-5 md:flex-row md:items-start md:justify-center md:gap-0">
            {systemSteps.map((step, index) => (
              <div key={step.num} className="contents">
                <div
                  className={`flex w-full min-w-0 flex-row items-center gap-4 md:flex-col md:justify-center md:gap-4 ${
                    index === 3 ? "md:w-[228px]" : index === 2 ? "md:w-[102px]" : "md:w-[140px]"
                  }`}
                >
                  <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-[#E0EAF4] bg-white/50">
                    <span className="font-sans text-[15px] font-medium leading-[18px] text-[#1B1C30]">
                      {step.num}
                    </span>
                  </div>
                  <span
                    className={`text-left font-display font-semibold text-[#4D4D5C] md:text-center ${
                      step.num === "1" ? "text-[15px] leading-5" : "text-[18px] leading-[23px]"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < systemSteps.length - 1 && (
                  <div className="hidden h-px w-24 shrink-0 translate-y-[17px] bg-[#E0EAF4] md:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// EVERYTHING YOU NEED SECTION
// ============================================
function EverythingYouNeed() {
  const features = [
    {
      title: "Market Intelligence",
      description: "Reviews + competitive analysis",
      icon: (
          <Image src="/everything/Frame 99.png" alt="Market Intelligence" width={36} height={36} />
      ),
    },
    {
      title: "Full Listing Creatives",
      description: "Main images, lifestyle images, A+ content",
      icon: (
          <Image src="/everything/Frame 99-1.png" alt="Full Listing Creatives" width={36} height={36} />
      ),
    },
    {
      title: "SERP Simulation",
      description: "See it your image stands out, fix if not",
      icon: (
          <Image src="/everything/Frame 99-2.png" alt="SERP Simulation" width={36} height={36} />
      ),
    },
    {
      title: "Winning Templates",
      description: "Clone A/B test winners",
      icon: (
          <Image src="/everything/Frame 99-3.png" alt="Winning Templates" width={36} height={36} />
      ),
    },
    {
      title: "Built in A/B Testing",
      description: "Split test with one click",
      icon: (
           <Image src="/everything/Frame 99-4.png" alt="A/B Testing" width={36} height={36} />
      ),
    },
    {
      title: "On-Brand Creatives",
      description: "Your fonts, colors, fonts, moods",
      icon: (
          <Image src="/everything/Frame 99-5.png" alt="On-Brand Creatives" width={36} height={36} />
      ),
    },
    {
      title: "Collaborate",
      description: "Invite your team",
      icon: (
          <Image src="/everything/Frame 99-6.png" alt="Collaborate" width={36} height={36} />
      ),
    },
    {
      title: "Main Image Generator",
      description: "CTR Focused Images",
      icon: (
           <Image src="/everything/Frame 99-7.png" alt="Main Image Generator" width={36} height={36} />
      ),
    },
  ];

  return (
    <section id="features" className="py-20" style={{ background: "#FAFBFD" }}>
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Heading */}
        <h2
          className="text-center mb-14"
          style={{
            fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
            fontSize: "clamp(28px, 3vw, 40px)",
            fontWeight: 700,
            color: "#1B1C30",
            letterSpacing: "-0.03em",
          }}
        >
          Everything You Need to Win on Amazon
        </h2>

        {/* 4x2 Feature Grid */}
        <div className="flex flex-wrap gap-4 mb-12">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="bg-white text-center flex flex-col py-12 rounded-xl p-5 border border-[#EEF0F6] hover:border-[#D4DAFB] transition-colors w-[calc(50%-8px)] md:w-[calc(25%-12px)]"
              style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}
            >
              <div className="mb-4 flex justify-center">{feature.icon}</div>
              <h4
                style={{
                  fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#1B1C30",
                  marginBottom: 4,
                }}
              >
                {feature.title}
              </h4>
              <p
                style={{
                  fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                  fontSize: 12,
                  color: "#8B8FA3",
                  lineHeight: 1.5,
                }}
              >
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <button
            onClick={() => {
              window.location.href = ROUTES.homePricing;
            }}
            className="inline-flex items-center gap-2 rounded-full transition-colors hover:bg-[#0D4FD6]"
            style={{
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
              fontSize: 14,
              fontWeight: 600,
              color: "#FFFFFF",
              background: "#1464FF",
              padding: "12px 28px",
            }}
          >
            Start Free
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M5.5 3L9.5 7L5.5 11"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}

// ============================================
// PRICING SECTION (moved to components/PricingSection.tsx)
// ============================================

// ============================================
// HOW IT WORKS DEMO SECTION
// ============================================
function HowItWorksDemo() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Heading */}
        <h2
          className="text-center mb-12"
          style={{
            fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
            fontSize: "clamp(28px, 3vw, 40px)",
            fontWeight: 700,
            color: "#1B1C30",
            letterSpacing: "-0.03em",
          }}
        >
          See How It Works in Just a Few Minutes
        </h2>

        {/* Browser Mockup */}
        <div className="relative max-w-[1200px] mx-auto">
          {/* Browser frame */}
          <div
            className="rounded-xl overflow-hidden"
            style={{
              border: "1px solid #E4E7EF",
              boxShadow: "0 8px 40px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
            }}
          >
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <AutoplayYouTube
                className="absolute inset-0 w-full h-full"
                videoId="L46x4un48co"
                title="Speedy Listing Demo"
                loop
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// TESTIMONIALS SECTION
// ============================================
function TestimonialsSection() {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const testimonials = [
    {
      name: "Brooke Hamilton",
      role: "Beacon Commerce",
      company: "Amazon Marketing Agency",
      avatar: "BH",
      avatarBg: "linear-gradient(135deg, #F59E0B, #EF4444)",
      text: "We are now able to split test faster for our clients. Main image generator is my favourite tool.",
      videoUrl: "https://youtube.com/shorts/fMJfR5qClw4",
      videoId: "fMJfR5qClw4",
      videoGradient: "from-blue-400 to-indigo-500",
    },
    {
      name: "Cameron Murphy",
      role: "Krampade",
      company: "8 Fig Brand",
      avatar: "CM",
      avatarBg: "linear-gradient(135deg, #8B5CF6, #6366F1)",
      text: "Saved 2k-4k per Amazon listing, highly recommended, go for 1 year plan.",
      videoUrl: "https://youtube.com/shorts/2IPuURZGJW4",
      videoId: "2IPuURZGJW4",
      videoGradient: "from-emerald-400 to-teal-500",
    },
    {
      name: "Tanya Higgs",
      role: "Luke & Lulu",
      company: "8 Fig Brand",
      avatar: "TH",
      avatarBg: "linear-gradient(135deg, #EC4899, #8B5CF6)",
      text: "It's 3x times faster than anything else. I love Speedy Listing and I use it for every single Amazon listing. Market Intelligence is my fav part.",
      videoUrl: "https://youtu.be/2NneEkEwSmw",
      videoId: "2NneEkEwSmw",
      videoGradient: "from-rose-400 to-pink-500",
    },
    {
      name: "Ashok Patel",
      role: "7 fig Amazon Seller",
      company: "",
      avatar: "AP",
      avatarBg: "linear-gradient(135deg, #14B8A6, #6366F1)",
      text: "Works really well for Amazon India. I was surprised with the image quality. Really good.",
      videoUrl: "https://youtube.com/shorts/_vCLZcTH-V0",
      videoId: "_vCLZcTH-V0",
      videoGradient: "from-amber-400 to-orange-500",
    },
    {
      name: "David Kohn",
      role: "Helps 7-8 Fig Amazon Sellers",
      company: "",
      avatar: "DK",
      avatarBg: "linear-gradient(135deg, #F59E0B, #14B8A6)",
      text: "I do full projects in 30 mins now and they are really good.",
      videoUrl: "https://youtube.com/shorts/T5Nz0UfEM1Y",
      videoId: "T5Nz0UfEM1Y",
      videoGradient: "from-cyan-400 to-blue-500",
    },
    {
      name: "Rowie",
      role: "Creative Director",
      company: "Amazon Agency",
      avatar: "R",
      avatarBg: "linear-gradient(135deg, #8B5CF6, #EC4899)",
      text: "We have successfully eliminated revisions from our process which were really expensive.",
      videoUrl: "https://youtube.com/shorts/bxbOHQpc82k",
      videoId: "bxbOHQpc82k",
      videoGradient: "from-violet-400 to-purple-500",
    },
  ];

  return (
    <section className="py-20" style={{ background: "linear-gradient(180deg, #FAFBFD 0%, #F4F5FA 100%)" }}>
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-4">
          <h2
            style={{
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
              fontSize: "clamp(28px, 3vw, 40px)",
              fontWeight: 700,
              color: "#1B1C30",
              letterSpacing: "-0.03em",
              lineHeight: 1.3,
            }}
          >
            Trusted by{" "}
            <span
              style={{
                textDecoration: "underline",
                textDecorationColor: "#1464FF",
                textUnderlineOffset: 4,
                textDecorationThickness: 2,
              }}
            >
              1,000+
            </span>{" "}
            Amazon Sellers & Agencies
          </h2>
          <p
            className="mt-3"
            style={{
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
              fontSize: 14,
              color: "#8B8FA3",
              lineHeight: 1.6,
            }}
          >
            See how brands and sellers are using Speedy Listing to create high-converting Amazon listings.
          </p>
        </div>

        {/* Testimonial Cards Grid - 3x2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="flex flex-col bg-white rounded-xl p-5 border border-[#EEF0F6] hover:border-[#D4DAFB] transition-all duration-300"
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.03)" }}
            >
              {/* Header: Avatar + Name + Role */}
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: t.avatarBg }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                      fontSize: 14,
                      fontWeight: 700,
                      color: "white",
                    }}
                  >
                    {t.avatar}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span
                    style={{
                      fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#1B1C30",
                    }}
                  >
                    {t.name}
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                      fontSize: 11,
                      color: "#8B8FA3",
                    }}
                  >
                    {t.role}{t.company ? `, ${t.company}` : ""}
                  </span>
                </div>
              </div>

              {/* Review text */}
              <p
                className="flex-1 mb-4"
                style={{
                  fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                  fontSize: 12,
                  color: "#5C5F73",
                  lineHeight: 1.6,
                }}
              >
                {t.text}
              </p>

              {/* Video thumbnail / Inline player */}
              {playingIndex === i ? (
                /* Inline YouTube player */
                <div className={`mt-auto relative aspect-[4/3] rounded-lg overflow-hidden bg-gradient-to-br ${t.videoGradient}`}>
                  <iframe
                    src={`https://www.youtube.com/embed/${t.videoId}?autoplay=1`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  {/* Close button */}
                  <button
                    onClick={() => setPlayingIndex(null)}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-white"
                    style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
                    aria-label="Close video"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2">
                      <path d="M1 1l10 10M11 1L1 11" />
                    </svg>
                  </button>
                </div>
              ) : (
                /* Thumbnail */
                <button
                  onClick={() => setPlayingIndex(i)}
                  className={`mt-auto block relative aspect-[4/3] rounded-lg overflow-hidden bg-gradient-to-br ${t.videoGradient} group w-full border-0 cursor-pointer`}
                >
                  <Image
                    src={`https://img.youtube.com/vi/${t.videoId}/mqdefault.jpg`}
                    alt={`${t.name} video testimonial`}
                    className="w-full h-full object-cover"
                    width={320}
                    height={180}
                  />
                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-110"
                      style={{
                        background: "rgba(255,255,255,0.25)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
                        <path d="M4 2.5V11.5L11.5 7L4 2.5Z" />
                      </svg>
                    </div>
                  </div>
                  {/* YouTube icon badge */}
                  <div className="absolute bottom-2 right-2">
                    <div
                      className="rounded-md px-2 py-1 flex items-center gap-1"
                      style={{
                        background: "rgba(0,0,0,0.55)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                      <span
                        style={{
                          fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                          fontSize: 10,
                          color: "white",
                          fontWeight: 500,
                        }}
                      >
                        Watch
                      </span>
                    </div>
                  </div>
                </button>
              )}

              {/* Stars */}
              <div className="flex items-center justify-end mt-3">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <svg key={s} className="w-3 h-3 text-amber-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div
          className="mt-10 rounded-full flex items-center justify-between px-6 py-3 mx-auto max-w-[600px]"
          style={{
            background: "white",
            border: "1px solid #EEF0F6",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          <div className="flex items-center gap-2">
            {/* Stacked mini avatars */}
            <div className="flex -space-x-1.5">
              {[
                { initials: "BH", bg: "linear-gradient(135deg, #F59E0B, #EF4444)" },
                { initials: "CM", bg: "linear-gradient(135deg, #8B5CF6, #6366F1)" },
                { initials: "TH", bg: "linear-gradient(135deg, #EC4899, #8B5CF6)" },
                { initials: "AP", bg: "linear-gradient(135deg, #14B8A6, #6366F1)" },
              ].map((img, i) => (
                <div
                  key={i}
                  className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center"
                  style={{ background: img.bg }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                      fontSize: 9,
                      fontWeight: 700,
                      color: "white",
                    }}
                  >
                    {img.initials}
                  </span>
                </div>
              ))}
            </div>
            <span
              style={{
                fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
                fontSize: 12,
                fontWeight: 500,
                color: "#5C5F73",
              }}
            >
              1,000+ Amazon sellers love Speedy Listing
            </span>
          </div>
          <button
            onClick={() => {
              window.location.href = ROUTES.homePricing;
            }}
            className="flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors hover:bg-[#0D4FD6]"
            style={{
              fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
              fontSize: 12,
              fontWeight: 600,
              color: "white",
              background: "#1464FF",
            }}
          >
            Start Free
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M4.5 2.5L8 6L4.5 9.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}

// ============================================
// CTA BANNER SECTION
// ============================================
function CtaBanner() {
  return (
    <section className="relative overflow-hidden" style={{ background: "#1464FF" }}>
      {/* Subtle diagonal light streaks */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          background:
            "repeating-linear-gradient(120deg, transparent, transparent 80px, rgba(255,255,255,0.4) 80px, rgba(255,255,255,0.4) 82px)",
        }}
      />
      <div className="relative z-10 max-w-[800px] mx-auto px-6 text-center py-20 md:py-28">
        <h2
          style={{
            fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
            fontSize: "clamp(28px, 3vw, 40px)",
            fontWeight: 700,
            color: "#FFFFFF",
            letterSpacing: "-0.03em",
            lineHeight: 1.25,
          }}
        >
          Struggling to Increase Amazon Sales?
        </h2>
        <p
          className="mt-4 mb-8"
          style={{
            fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
            fontSize: 16,
            color: "rgba(255,255,255,0.8)",
            lineHeight: 1.6,
          }}
        >
          Let AI do the heavy lifting. Generate scroll-stopping images and A+ content that converts.
        </p>
        <button
          onClick={() => {
            window.location.href = ROUTES.homePricing;
          }}
          className="rounded-full transition-all hover:brightness-95 hover:scale-[1.02]"
          style={{
            fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
            fontSize: 15,
            fontWeight: 600,
            color: "#1464FF",
            background: "#FFFFFF",
            padding: "14px 36px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          }}
        >
          Get Started Free
        </button>
      </div>
    </section>
  );
}

// ============================================
// USER CREATIONS SHOWCASE SECTION
// ============================================
function UserCreationsShowcase() {
  const userCreatedImages = [
    "/other user are creating/Frame 2147226403.png",
    "/other user are creating/Frame 2147226404.png",
    "/other user are creating/Frame 2147226405.png",
    "/other user are creating/Frame 2147226406.png",
    "/other user are creating/Frame 2147226407.png",
    "/other user are creating/Frame 2147226403-1.png",
    "/other user are creating/Frame 2147226404-1.png",
    "/other user are creating/Frame 2147226405-1.png",
    "/other user are creating/Frame 2147226406-1.png",
    "/other user are creating/Frame 2147226407-1.png",
    "/other user are creating/Frame 2147226403-2.png",
    "/other user are creating/Frame 2147226404-2.png",
    "/other user are creating/Frame 2147226405-2.png",
    "/other user are creating/Frame 2147226406-2.png",
    "/other user are creating/Frame 2147226407-2.png",
    "/other user are creating/Frame 2147226403-3.png",
    "/other user are creating/Frame 2147226404-3.png",
    "/other user are creating/Frame 2147226405-3.png",
    "/other user are creating/Frame 2147226406-3.png",
    "/other user are creating/Frame 2147226407-3.png",
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Heading */}
        <h2
          className="text-center mb-10"
          style={{
            fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif",
            fontSize: "clamp(28px, 3vw, 40px)",
            fontWeight: 700,
            color: "#1B1C30",
            letterSpacing: "-0.03em",
          }}
        >
          See What Other Users Are Creating
        </h2>

        {/* 4-column image grid - 5 rows with 20 images */}
        <div className="flex flex-wrap gap-4">
          {userCreatedImages.map((src, i) => (
            <div
              key={i}
              className="aspect-square rounded-xl overflow-hidden relative group w-[calc(50%-8px)] md:w-[calc(25%-12px)]"
              style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
            >
              <Image
                src={src}
                alt={`User creation ${i + 1}`}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// MAIN PAGE
// ============================================
export default function Home() {
  return (
    <>
      <HeroSection />
      <ClientLogos title="Trusted by top Amazon brands" />
      <StatsSection />
      <HowItWorks />
      <ListingShowcase />
      <CustomerReviews />
      <APlusModules />
      <ProductVariantsSection />
      <AmazonExperts />
      <EverythingYouNeed />
      <PricingSection />
      <EnterpriseContact />
      <HowItWorksDemo />
      <div id="how-it-works-demo" />
      <TestimonialsSection />
      <CtaBanner />
      <UserCreationsShowcase />
      <MarketplaceOptimizationSection />
      <FAQSection
        description="Everything you need to know about creating Amazon listing creative with Speedy Listing."
        faqs={homeFAQItems}
      />
      <NewsletterSignupSection />
    </>
  );
}
